export default class PromoSlider {
  protected slider: HTMLElement;

  protected sliderContainer: HTMLElement;

  protected items: HTMLElement[];

  protected controls: {
    prev: HTMLButtonElement | null;
    next: HTMLButtonElement | null;
    bullets: HTMLButtonElement[];
  };

  protected currentSlide: number = 0;

  protected isDragging: boolean = false;

  protected startPos = { x: 0, y: 0 };

  protected currentTranslate: number = 0;

  protected prevTranslate: number = 0;

  protected gap: number = 30; // Значение gap из CSS

  protected isHorizontalScroll: boolean = false;

  protected touchStartTime: number = 0;

  protected isTouchMove: boolean = false;

  constructor(slider: string, initControls: boolean = true) {
    this.slider = document.querySelector(slider) as HTMLElement;
    this.sliderContainer = this.slider.firstElementChild as HTMLElement;
    this.setGap();
    this.items = Array.from(this.sliderContainer.children) as HTMLElement[];

    // Инициализируем контролы только если нужно
    if (initControls) {
      const controlsContainer = this.slider.lastElementChild as HTMLElement;
      const prevButton = controlsContainer.firstElementChild as HTMLButtonElement;
      const nextButton = controlsContainer.lastElementChild as HTMLButtonElement;

      // Получаем все буллеты, начиная с элемента после prevButton
      const bullets: HTMLButtonElement[] = [];
      let currentBullet = prevButton.nextElementSibling as HTMLButtonElement;

      // Собираем все буллеты до nextButton
      for (let i = 0; i < this.items.length; i++) {
        if (currentBullet) {
          bullets.push(currentBullet);
          currentBullet = currentBullet.nextElementSibling as HTMLButtonElement;
        }
      }

      this.controls = {
        prev: prevButton,
        next: nextButton,
        bullets: bullets,
      };
    } else {
      // Пустые контролы если они не нужны
      this.controls = {
        prev: null,
        next: null,
        bullets: [],
      };
    }

    this.init();
  }

  protected setGap = () => {
    const gapValue = getComputedStyle(this.sliderContainer).gap;
    if (gapValue) this.gap = parseInt(gapValue);
  };

  protected init = (): void => {
    // Добавляем обработчики для кнопок только если они есть
    if (this.controls.prev && this.controls.next) {
      this.controls.prev.addEventListener('click', () => this.slide('prev'));
      this.controls.next.addEventListener('click', () => this.slide('next'));
      this.controls.bullets.forEach((bullet, index) => {
        bullet.addEventListener('click', () => this.goToSlide(index));
      });
    }

    // Добавляем обработчики для свайпов
    this.sliderContainer.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.sliderContainer.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.sliderContainer.addEventListener('touchend', this.handleTouchEnd);
    this.sliderContainer.addEventListener('touchcancel', this.handleTouchEnd);

    // Добавляем обработчики для перетаскивания мышью
    this.sliderContainer.addEventListener('mousedown', this.handleMouseDown);
    this.sliderContainer.addEventListener('mousemove', this.handleMouseMove);
    this.sliderContainer.addEventListener('mouseup', this.handleMouseUp);
    this.sliderContainer.addEventListener('mouseleave', this.handleMouseUp); // Добавим обработку выхода мыши за пределы

    // Обновляем gap при изменении размера окна
    window.addEventListener('resize', () => {
      this.setGap();
      this.goToSlide(this.currentSlide);
    });
  };

  private handleTouchStart = (e: TouchEvent): void => {
    if (e.touches.length !== 1) return;

    this.touchStartTime = Date.now();
    this.isTouchMove = false;
    this.isDragging = true;
    this.isHorizontalScroll = false;
    this.startPos = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    this.sliderContainer.style.transition = 'none';
  };

  private handleTouchMove = (e: TouchEvent): void => {
    if (!this.isDragging || e.touches.length !== 1) return;

    this.isTouchMove = true;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - this.startPos.x;
    const diffY = currentY - this.startPos.y;

    // Определяем направление свайпа только при первом движении
    if (!this.isHorizontalScroll) {
      // Если горизонтальное движение больше вертикального, считаем свайп горизонтальным
      this.isHorizontalScroll = Math.abs(diffX) > Math.abs(diffY);

      // Если свайп вертикальный, прекращаем обработку
      if (!this.isHorizontalScroll) {
        this.isDragging = false;
        return;
      }
    }

    // Предотвращаем скролл только при горизонтальном свайпе
    if (this.isHorizontalScroll) {
      e.preventDefault();
      this.updateSlidePosition(diffX);
    }
  };

  private handleTouchEnd = (): void => {
    if (!this.isDragging) return;

    // Проверяем, был ли это быстрый тап (без движения)
    const touchTime = Date.now() - this.touchStartTime;
    if (!this.isTouchMove && touchTime < 300) {
      // Это был тап - ничего не делаем
      this.isDragging = false;
      return;
    }

    this.finishSlideMovement();
  };

  private handleMouseDown = (e: MouseEvent): void => {
    e.preventDefault(); // Предотвращаем выделение текста
    this.isDragging = true;
    this.isHorizontalScroll = true;
    this.startPos = {
      x: e.clientX,
      y: e.clientY,
    };
    this.sliderContainer.style.transition = 'none';
  };

  private handleMouseMove = (e: MouseEvent): void => {
    if (!this.isDragging) return;

    e.preventDefault();
    const diffX = e.clientX - this.startPos.x;
    this.updateSlidePosition(diffX);
  };

  private handleMouseUp = (): void => {
    if (!this.isDragging) return;
    this.finishSlideMovement();
  };

  private updateSlidePosition = (diff: number): void => {
    this.currentTranslate = this.prevTranslate + diff;

    // Ограничиваем перемещение
    const maxTranslate = -(this.items.length - 1) * (this.items[0].offsetWidth + this.gap);
    this.currentTranslate = Math.max(Math.min(this.currentTranslate, 0), maxTranslate);

    this.setSliderPosition(this.currentTranslate);
  };

  private finishSlideMovement = (): void => {
    this.isDragging = false;
    const movedBy = this.currentTranslate - this.prevTranslate;

    if (this.isHorizontalScroll && Math.abs(movedBy) > 50) {
      if (movedBy < 0) {
        this.slide('next');
      } else {
        this.slide('prev');
      }
    } else {
      this.goToSlide(this.currentSlide);
    }
  };

  private slide = (direction: 'prev' | 'next'): void => {
    if (direction === 'prev' && this.currentSlide > 0) {
      this.goToSlide(this.currentSlide - 1);
    } else if (direction === 'next' && this.currentSlide < this.items.length - 1) {
      this.goToSlide(this.currentSlide + 1);
    }
  };

  protected goToSlide(index: number): void {
    this.currentSlide = index;
    const slideWidth = this.items[0].offsetWidth;
    this.currentTranslate = -(slideWidth + this.gap) * index;
    this.prevTranslate = this.currentTranslate;

    this.sliderContainer.style.transition = 'transform 0.3s ease-out';
    this.setSliderPosition(this.currentTranslate);

    // Обновляем состояние буллетов только если они есть
    if (this.controls.bullets.length > 0) {
      this.controls.bullets.forEach((bullet, i) => {
        bullet.classList.toggle('active', i === index);
      });
    }
  }

  private setSliderPosition = (position: number): void => {
    requestAnimationFrame(() => {
      this.sliderContainer.style.transform = `translateX(${position}px)`;
    });
  };
}
